// A table = one sim + a bot brain in every seat no person is driving. The server's room, the browser's offline driver
// and the headless gates all run exactly this, so a round plays out the same everywhere.
//
// Each step: every bot reads the previous step's views/events for its seat and answers with inputs, then the sim steps.
import { Sim, type SimEvent, type PublicView, type Cosmetics } from "./sim";
import { BotSneak, BotInspector, type BotBrain, type SneakParams } from "./bots";

export type BrainFactory = (seat: number, role: "sneak" | "inspector", seed: number) => BotBrain;
export const defaultBrains: BrainFactory = (_seat, role, seed) => (role === "sneak" ? new BotSneak(seed) : new BotInspector(seed));
/** a factory that puts a custom Sneak brain in one seat (the human-model gate) and default bots elsewhere */
export const withSneak = (seat: number, params: SneakParams): BrainFactory => (s, role, seed) => (s === seat && role === "sneak" ? new BotSneak(seed, params) : defaultBrains(s, role, seed));

/** only what a seat may see: public events plus its own private ones */
export const eventsFor = (evs: SimEvent[], seat: number) => evs.filter((e) => !("to" in e) || e.to === seat);

export class Table {
  sim: Sim;
  /** seats a connected person drives right now (a dropped person's seat is driven by a bot until they return) */
  driven: boolean[];
  private brains: (BotBrain | null)[];
  private brainRound: number[];
  private last: SimEvent[] = [];
  private seed: number;
  pub: PublicView;

  constructor(seed: number, opts: { wallMs?: number; brains?: BrainFactory } = {}) {
    this.seed = seed;
    this.sim = new Sim(seed, { wallMs: opts.wallMs });
    this.driven = this.sim.seats.map(() => false);
    this.brains = this.sim.seats.map(() => null);
    this.brainRound = this.sim.seats.map(() => -1);
    this.factory = opts.brains || defaultBrains;
    this.last = [...this.sim.events];
    this.pub = this.sim.publicView();
  }
  factory: BrainFactory;

  /** a person takes seat i (newcomer: their first visit → an onboarding round next) */
  seatHuman(i: number, info: { name?: string; pid?: string; cos?: Cosmetics; newcomer?: boolean }) { this.sim.setHuman(i, true, info); this.driven[i] = true; }
  /** the person left for good: a bot takes the seat back (same body, same role) */
  freeSeat(i: number) { this.sim.setHuman(i, false); this.driven[i] = false; this.brains[i] = null; }
  /** a dropped connection: a bot keeps the body looking normal until the person is back */
  setDriven(i: number, on: boolean) { this.driven[i] = on; if (on) this.brains[i] = null; else this.sim.setInput(i, { mx: 0, mz: 0, sprint: false, act: false }); }
  /** the seat a newcomer takes: a bot Sneak's; only when every Sneak seat is a person's, the bot Inspector's */
  freeSeatIndex(): number {
    const i = this.sim.seats.findIndex((s) => !s.human && s.i !== this.sim.inspectorSeat);
    return i >= 0 ? i : this.sim.seats.findIndex((s) => !s.human);
  }

  step(): SimEvent[] {
    const sim = this.sim, pub = this.pub;
    for (const s of sim.seats) {
      if (this.driven[s.i]) continue;
      let b = this.brains[s.i];
      if (!b || b.role !== s.role || this.brainRound[s.i] !== sim.round) {
        b = this.brains[s.i] = this.factory(s.i, s.role, (this.seed * 7919 + s.i * 104729 + sim.round * 15485863) >>> 0);
        this.brainRound[s.i] = sim.round;
      }
      // a newcomer's reserved capsule is invisible to the bots (sim.ts first-minute rules)
      const held = sim.reservedStall >= 0 && sim.time < sim.reserveUntil && s.i !== sim.newcomerSeat && pub.stalls[sim.reservedStall];
      const bp = held ? { ...pub, stalls: pub.stalls.map((v, k) => (k === sim.reservedStall ? false : v)) } : pub;
      const out = b.update(bp, sim.privateView(s.i), eventsFor(this.last, s.i));
      sim.setInput(s.i, out.input);
      for (const p of out.pops) sim.pop(s.i, p.id, p.aim);
      if (out.whistle) sim.whistleCall(s.i);
    }
    sim.step();
    this.last = sim.events;
    this.pub = sim.publicView();
    return sim.events;
  }
}
